/**
 * @file Ecran Modes : cartes des modes (verrouille / tickets / record / statut squelette), lancement et balayage.
 */
import type { ModeCardView } from '@core/index';
import { stageValueToDisplay } from '@core/index';
import type { UnlockRule } from '@config/types';
import type { ModeId } from '@shared/enums';
import { formatBig, formatDuration } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { closeScreen, send } from '../core/feedback';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import { sheetHeader, walletChips } from '../core/widgets';
import type { UiContext } from '../UiContext';

const ICONS: Record<ModeId, UiIconId> = {
  main: 'tree',
  speedRun: 'stopwatch',
  mine: 'pickaxe',
  boss: 'boss',
  monster: 'monster',
  raid: 'dragon',
  adventure: 'compass',
  fish: 'fish',
  harvest: 'log',
  hub: 'hub',
};

interface CardView {
  id: ModeId;
  root: HTMLElement;
  lock: HTMLElement;
  lockText: HTMLElement;
  tickets: HTMLElement;
  ticketsText: HTMLElement;
  best: HTMLElement;
  play: HTMLElement;
  sweep: HTMLElement;
}

export class ModesScreen extends Component {
  private readonly cards: CardView[] = [];
  private ticketTotal!: HTMLElement;
  private ticketTotalText!: HTMLElement;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen modes');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    const list = h('div', { class: 'mode-list' });
    for (const card of this.ctx.sim.query.modes()) {
      const def = card.def;
      if (def.id === 'main') continue;
      const lockText = h('span');
      const lock = h('div', { class: 'mode-lock' }, uiIcon('lock'), lockText);
      const ticketsText = h('span');
      const tickets = h('div', { class: 'mode-tickets' }, uiIcon('ticket'), ticketsText);
      const best = h('div', { class: 'mode-best' });
      const play = h('button', { class: 'btn primary', type: 'button', 'data-ui-block': true, onclick: () => this.start(def.id, false) }, t.t('modes.play'));
      const sweep = h('button', { class: 'btn', type: 'button', 'data-ui-block': true, onclick: () => this.start(def.id, true) }, t.t('modes.sweep'));
      const root = h(
        'div',
        { class: `mode-card panel mode-${def.id}`, 'data-mode': def.id },
        h(
          'div',
          { class: 'mode-head' },
          h('span', { class: 'mode-icon' }, uiIcon(ICONS[def.id])),
          h('b', { class: 'mode-title' }, t.tDyn(`mode.${def.id}.title`)),
          def.status === 'skeleton' ? h('span', { class: 'badge-pill' }, t.t('modes.skeleton')) : null,
        ),
        h('div', { class: 'mode-info' }, tickets, best, ...walletChips(this.ctx, def.entry.cost)),
        def.id === 'boss' || def.id === 'raid' ? h('div', { class: 'hint mode-hint' }, t.t(def.id === 'boss' ? 'modes.bossHint' : 'modes.raidHint')) : null,
        lock,
        h('div', { class: 'btn-row' }, play, sweep),
      );
      this.cards.push({ id: def.id, root, lock, lockText, tickets, ticketsText, best, play, sweep });
      list.append(root);
    }
    // total des tickets de modes, a cote du titre du panneau
    const head = sheetHeader(this.ctx, t.t('modes.title'));
    const title = head.querySelector('.sheet-title');
    this.ticketTotalText = h('span', {}, '0');
    this.ticketTotal = h('span', { class: 'ticket-total', title: t.t('menu.tickets') }, uiIcon('ticket'), this.ticketTotalText);
    if (title) {
      const row = h('div', { class: 'sheet-title-row' });
      title.replaceWith(row);
      row.append(title, this.ticketTotal);
    }
    this.el.append(head, h('div', { class: 'sheet-body' }, list));
    this.update(0);
  }

  private start(mode: ModeId, sweep: boolean): void {
    const r = send(this.ctx, sweep ? { type: 'mode.start', mode, params: { sweep: true } } : { type: 'mode.start', mode });
    if (r.ok) closeScreen();
  }

  private lockText(rule: UnlockRule): string {
    const { i18n, config } = this.ctx;
    switch (rule.kind) {
      case 'stageValue':
        return i18n.t('common.unlockAt', { stage: stageValueToDisplay(config.balance, rule.atLeast).stage });
      case 'rebirths':
        return i18n.t('common.unlockRebirths', { n: rule.atLeast });
      default:
        return i18n.t('common.locked');
    }
  }

  override update(_dt: number): void {
    const views = new Map<ModeId, ModeCardView>();
    for (const v of this.ctx.sim.query.modes()) views.set(v.def.id, v);
    const { i18n } = this.ctx;
    setText(this.ticketTotalText, `${Object.values(this.ctx.sim.data.tickets).reduce((n, tk) => n + tk.count, 0)}`);
    for (const c of this.cards) {
      const v = views.get(c.id);
      if (!v) continue;
      const noTickets = v.tickets !== null && v.tickets <= 0;
      toggleClass(c.root, 'locked', !v.unlocked);
      toggleClass(c.lock, 'is-hidden', v.unlocked);
      if (!v.unlocked) setText(c.lockText, this.lockText(v.def.entry.unlock));
      toggleClass(c.tickets, 'is-hidden', v.tickets === null || !v.unlocked);
      if (v.tickets !== null) {
        const next = v.nextTicketIn !== null && Number.isFinite(v.nextTicketIn) ? ` · ${i18n.t('modes.nextTicket', { time: formatDuration(v.nextTicketIn) })}` : '';
        setText(c.ticketsText, `${i18n.t('modes.tickets', { n: v.tickets })}${next}`);
      }
      const hasBest = v.best !== null && v.best > 0;
      if (c.id === 'boss' || c.id === 'raid') {
        // Boss / Raid : on affiche le niveau a affronter (victoires + 1) et le record
        const next = (v.best ?? 0) + 1;
        toggleClass(c.best, 'is-hidden', !v.unlocked);
        setText(c.best, hasBest && v.best !== null ? `${i18n.t('modes.level', { n: next })} · ${i18n.t('modes.best', { best: formatBig(v.best) })}` : i18n.t('modes.level', { n: next }));
      } else {
        toggleClass(c.best, 'is-hidden', !hasBest || !v.unlocked);
        if (hasBest && v.best !== null) setText(c.best, i18n.t('modes.best', { best: formatBig(v.best) }));
      }
      toggleClass(c.play, 'disabled', !v.unlocked || noTickets);
      toggleClass(c.sweep, 'is-hidden', !(v.def.entry.sweep === true && hasBest && v.unlocked));
      toggleClass(c.sweep, 'disabled', noTickets);
    }
  }
}
